// TODO: vitest component test (pages are covered by Playwright e2e)
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Tag } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, DataTable, Skeleton } from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { useRewardCatalogItems, type RewardCatalogItem } from '@/hooks/useRewardCampaigns'
import { formatBaht } from '@/lib/money'

export function CatalogListPage() {
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()

  const { data, isLoading, isFetching, error } = useRewardCatalogItems()
  const items = data?.data ?? []

  const columns: Column<RewardCatalogItem>[] = [
    {
      key: 'name',
      header: t('catalog.fields.name'),
      render: (i) => <span className="font-medium">{i.name}</span>,
    },
    {
      key: 'reward_type',
      header: t('catalog.fields.reward_type'),
      render: (i) => t(`catalog.reward_type.${i.reward_type}`),
    },
    {
      key: 'cost_points',
      header: t('catalog.fields.cost_points'),
      render: (i) => i.cost_points,
      hideBelow: 'md',
    },
    {
      key: 'thb_value',
      header: t('catalog.fields.thb_value'),
      render: (i) => formatBaht(i.thb_value),
      hideBelow: 'md',
    },
    {
      key: 'stock',
      header: t('catalog.fields.stock'),
      render: (i) => i.stock ?? '—',
      hideBelow: 'lg',
    },
    {
      key: 'active',
      header: t('catalog.fields.active'),
      render: (i) => (
        <Badge variant={i.active ? 'success' : 'ghost'}>
          {i.active ? tc('status.active') : tc('status.inactive')}
        </Badge>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('catalog.title') }]} />

      <PageHeader
        title={t('catalog.title')}
        description={t('catalog.subtitle')}
        isRefreshing={isFetching && !isLoading}
        actions={
          <Button size="sm" onClick={() => navigate('/incentives/campaigns')}>
            {tc('actions.view')} {t('campaigns.title')}
          </Button>
        }
      />

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={tc('errors.something_went_wrong')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : !items.length ? (
        <Card>
          <EmptyState
            icon={<Tag className="w-12 h-12" />}
            title={t('catalog.empty.title')}
            description={t('catalog.empty.description')}
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={items}
            keyExtractor={(i) => i.id}
            onRowClick={(i) => navigate(`/incentives/campaigns/${i.campaign_id}`)}
          />
        </Card>
      )}
    </div>
  )
}
